import React from 'react';
import { MapPinIcon, ClockIcon, GlobeAltIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { Instagram } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

interface Restaurant {
  address?: string;
  opening_hours: string;
  phone?: string;
  website?: string;
  instagram?: string;
  mealTime?: string[];
  food_types?: string[];
  category: string;
  latitude?: number;
  longitude?: number;
}

interface RestaurantInfoProps {
  restaurant: Restaurant;
  addressFirst?: boolean;
  placeId?: string;
  placeName?: string;
}

const RestaurantInfo: React.FC<RestaurantInfoProps> = ({
  restaurant,
  addressFirst = false,
  placeId,
  placeName,
}) => {
  // ساخت لینک گوگل‌مپ از مختصات یا آدرس
  const googleMapsLink =
    restaurant.latitude && restaurant.longitude
      ? `https://www.google.com/maps?q=${restaurant.longitude},${restaurant.latitude}`
      : restaurant.address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          restaurant.address
        )}`
      : null;

  const addressRow = restaurant.address ? (
    <div className="flex items-start gap-2 py-3 font-myYekanDemibold">
      <MapPinIcon strokeWidth={2} className="mt-1 h-5 w-5 flex-shrink-0 text-[#222222]" />
      {googleMapsLink ? (
        <a
          href={googleMapsLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent("click_navigation", {
            place_id: placeId || "unknown",
            place_name: placeName || "unknown",
            surface: "general_info_address",
          })}
          className="text-[#068646] hover:underline"
        >
          {restaurant.address}
        </a>
      ) : (
        <span>{restaurant.address}</span>
      )}
    </div>
  ) : null;

  return (
    <section className="rounded-xl border-b border-gray-200 px-0 py-4 pb-3 tablet:border-b-0 tablet:px-0 tablet:py-6">
      <h2 className="mb-4 hidden text-[1.09rem] font-myYekanFaNumExtraBold leading-[1.5] text-[#222222] tablet:block">
        اطلاعات عمومی
      </h2>
      <div className="divide-y divide-gray-200 text-[0.77rem] leading-[1.8] text-[#222222] font-myYekanRegular tablet:text-gray-600">

        {addressFirst && addressRow}

        {/* Phone */}
        {restaurant.phone && (
          <div className="flex items-center gap-2 py-3">
            <PhoneIcon strokeWidth={2} className="h-5 w-5 flex-shrink-0 -scale-x-100 text-[#222222]" aria-hidden="true" />
            <a
              href={`tel:${restaurant.phone}`}
              onClick={() => trackEvent("click_contact_phone", {
                place_id: placeId || "unknown",
                place_name: placeName || "unknown",
                surface: "general_info",
              })}
              className="font-myYekanFaNumDemiBold hover:underline"
            >
              {restaurant.phone}
            </a>
          </div>
        )}

        {/* Opening Hours */}
        {restaurant.opening_hours && (
          <div className="flex items-center gap-2 py-3" dir="rtl">
            <ClockIcon strokeWidth={2} className="h-5 w-5 flex-shrink-0 text-[#222222]" />
            <span className="font-myYekanFaNumDemiBold">{restaurant.opening_hours}</span>
          </div>
        )}

        {/* Address */}
        {!addressFirst && addressRow}

        {/* Socials and Website */}
        {(restaurant.instagram || restaurant.website) && (
        <div className="flex flex-row flex-wrap items-center gap-4 py-3">
          {restaurant.instagram && (
            <div className="flex items-center gap-2">
              <Instagram size={18} strokeWidth={2} className="shrink-0 text-[#222222]" aria-hidden="true" />
              <a
                href={
                  restaurant.instagram.startsWith('http')
                    ? restaurant.instagram
                    : `https://instagram.com/${restaurant.instagram.replace(/^@/, '')}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="font-myYekanDemibold hover:underline"
              >
                اینستاگرام
              </a>
            </div>
          )}

          {restaurant.website && (
            <div className="flex items-center gap-2">
              <GlobeAltIcon className="h-5 w-5" />
              <a
                href={
                  restaurant.website.startsWith('http')
                    ? restaurant.website
                    : `https://${restaurant.website}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                وبسایت
              </a>
            </div>
          )}
        </div>
        )}
      </div>
    </section>
  );
};

export default RestaurantInfo;
